"use client";

import { isCmsShowFlagOn } from "@/utils/variantHelpers";
import type { CSSProperties } from "react";

/**
 * Header 5016 — KUK promotional marquee.
 *
 * Port of the promotional strip at the top of kuk-campus-portal
 * `components/Landing/Hero.tsx`: a tinted bar carrying one line of promotional
 * copy that scrolls continuously across it.
 *
 * WHY IT IS ITS OWN SECTION — v1 renders this inside the hero component, so the
 * strip cannot exist without a hero and cannot be reordered, hidden or reworded
 * independently of one. This app already treats the promotional strip as a page
 * section (Header 3, Promo Pill 5000), so Banner 5003 leaves it out and this
 * carries it.
 *
 * THE SCROLL — v1 uses a `Marquee` component that duplicates its children and
 * translates the pair, which is the standard trick for a seamless loop: the copy
 * is rendered twice and the track slides by exactly half its width, so the moment
 * the first copy leaves the frame the second is in the identical position.
 * Reproduced with a CSS animation rather than JS — no measurement, nothing to
 * recalculate on resize, and it keeps running before hydration.
 *
 * REDUCED MOTION — the animation is disabled under `prefers-reduced-motion`. v1
 * scrolls unconditionally; an indefinitely moving band of text is exactly what
 * that preference exists to stop, and the copy stays fully legible standing
 * still.
 *
 * DROPPED — v1's `urgency-bar-backdrop.png`, a decorative texture positioned
 * with a fixed `88px` background size against a `55px` box, so it is cropped at
 * every width and reads as a faint smear behind the tint. It is available as an
 * optional background image rather than baked in.
 *
 * DROPPED — v1's `bg-opacity-80` on the bar, which it applies on top of an
 * already-opaque hex. The fill is a single CMS colour here; an editor who wants
 * it translucent sets an RGBA value, which is what the reference's pairing
 * actually resolves to.
 */

/**
 * v1: `speed="fast"` on the marquee, and a `gap-36 md:gap-80` between copies.
 * Its `Marquee` maps the three speeds to `scroll_60s` / `scroll_50s` / `scroll_40s`,
 * so "fast" is a 40-second pass — which is what the live bar renders.
 */
const DEFAULT_DURATION_MS = 40000;

interface Header5016Data {
  status?: boolean | number | string;
  /** The scrolling copy. */
  promotional_text?: string;
  promotional_text_color?: string;
  promotional_text_bg_color?: string;
  /** Milliseconds for one full pass. */
  auto_scroll_speed?: number | string;
  /** Optional decorative texture behind the tint. */
  background_gradient_angle?: string;
  logo?: string;
  logo_alt_text?: string;
  sticky_to_top?: boolean | number | string;
  show_on_mobile?: boolean | number | string;
}

const Header5016 = (props: { data?: Header5016Data }) => {
  const { data: raw } = props;
  const sectionData = ((raw as any)?.data ?? raw) as Header5016Data | undefined;

  const {
    promotional_text = "",
    promotional_text_color = "#FFFFFF",
    promotional_text_bg_color = "rgba(181, 32, 60, 0.80)",
    auto_scroll_speed = DEFAULT_DURATION_MS,
    logo: background_image = "",
    logo_alt_text = "",
  } = sectionData || {};

  const status = isCmsShowFlagOn(sectionData?.status, true);
  const stickyToTop = isCmsShowFlagOn(sectionData?.sticky_to_top, false);
  const showOnMobile = isCmsShowFlagOn(sectionData?.show_on_mobile, true);

  const text = (promotional_text || "").trim();
  if (!status || !text) return null;

  const duration = Number(auto_scroll_speed) || DEFAULT_DURATION_MS;

  return (
    <div
      className={[
        "relative w-full overflow-hidden",
        showOnMobile ? "" : "hidden tab:block",
        stickyToTop ? "sticky top-0 z-40" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      style={
        {
          backgroundColor: promotional_text_bg_color,
          ...(background_image
            ? {
                backgroundImage: `url("${background_image.replace(/"/g, "%22")}")`,
                backgroundRepeat: "no-repeat",
                backgroundPosition: "center top",
                backgroundSize: "cover",
              }
            : {}),
          "--kuk-marquee-duration": `${duration}ms`,
        } as CSSProperties
      }
      aria-label={logo_alt_text || undefined}
    >
      {/* Scoped to this widget's own class names so it cannot collide with
          another marquee on the page. */}
      <style>{`
        @keyframes kukMarquee5016 {
          from { transform: translateX(0); }
          to   { transform: translateX(-50%); }
        }
        .kuk-marquee-5016-track {
          animation: kukMarquee5016 var(--kuk-marquee-duration, 40000ms) linear infinite;
        }
        @media (prefers-reduced-motion: reduce) {
          .kuk-marquee-5016-track { animation: none; }
        }
      `}</style>

      <div className="flex w-full py-2">
        <div className="kuk-marquee-5016-track flex w-max shrink-0 items-center">
          {/* Rendered twice: the track slides exactly half its width, so the
              second copy lands where the first began and the loop is seamless.
              The duplicate is decorative, hence aria-hidden. */}
          <p
            className="whitespace-nowrap px-16 text-lg font-normal tab:px-40 tab:text-xl"
            style={{ color: promotional_text_color }}
          >
            {text}
          </p>
          <p
            aria-hidden
            className="whitespace-nowrap px-16 text-lg font-normal tab:px-40 tab:text-xl"
            style={{ color: promotional_text_color }}
          >
            {text}
          </p>
        </div>
      </div>
    </div>
  );
};

export default Header5016;
